/**
 * Osquery query library.
 *
 * Replaces the array editor that saved the whole query list as one JSON blob,
 * where the last admin to hit Save overwrote everyone else's edits and every
 * tenant shared one list. Each query is now a row that is saved on its own.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Eye,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { apiService, OsqueryLibraryQuery } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PLATFORM_LABELS: Record<string, string> = {
  windows: 'Windows',
  linux: 'Linux',
  darwin: 'macOS',
};

const CATEGORY_STYLES: Record<string, string> = {
  general: 'bg-slate-100 text-slate-700',
  performance: 'bg-blue-100 text-blue-700',
  compliance: 'bg-purple-100 text-purple-700',
  security: 'bg-red-100 text-red-700',
  inventory: 'bg-emerald-100 text-emerald-700',
  network: 'bg-amber-100 text-amber-700',
};

interface DraftQuery {
  id?: string;
  name: string;
  sql: string;
  description: string;
  category: string;
  platforms: string[];
  interval_minutes: number;
  enabled: boolean;
}

const EMPTY_DRAFT: DraftQuery = {
  name: '',
  sql: 'SELECT * FROM system_info',
  description: '',
  category: 'general',
  platforms: [],
  interval_minutes: 15,
  enabled: true,
};

const errorText = (err: unknown, fallback: string): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return fallback;
};

const OsqueryQueryLibrary: React.FC = () => {
  const { can } = useAuth();
  const canEdit = can('settings.tenant.edit');

  const [queries, setQueries] = useState<OsqueryLibraryQuery[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [draft, setDraft] = useState<DraftQuery | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const [preview, setPreview] = useState<{ platform: string; names: string[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getOsqueryLibrary();
      setQueries(res.queries || []);
      setCategories(res.categories || []);
      setPlatforms(res.platforms || []);
    } catch (err) {
      setError(errorText(err, 'Failed to load the query library'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 3000);
  };

  const visible = useMemo(
    () =>
      queries.filter((q) => {
        if (categoryFilter && q.category !== categoryFilter) return false;
        if (platformFilter && q.platforms.length > 0 && !q.platforms.includes(platformFilter)) {
          return false;
        }
        return true;
      }),
    [queries, categoryFilter, platformFilter]
  );

  const grouped = useMemo(() => {
    const byCategory = new Map<string, OsqueryLibraryQuery[]>();
    visible.forEach((q) => {
      const list = byCategory.get(q.category) || [];
      list.push(q);
      byCategory.set(q.category, list);
    });
    return Array.from(byCategory.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visible]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const patchQuery = async (query: OsqueryLibraryQuery, updates: Partial<OsqueryLibraryQuery>) => {
    setBusyId(query.id);
    setError(null);
    try {
      const updated = await apiService.updateOsqueryLibraryQuery(query.id, updates);
      setQueries((prev) => prev.map((q) => (q.id === query.id ? { ...q, ...updated } : q)));
      flash(`Saved "${query.name}". Agents pick this up on their next config poll.`);
    } catch (err) {
      setError(errorText(err, 'Failed to save the query'));
    } finally {
      setBusyId(null);
    }
  };

  const clearOverride = async (query: OsqueryLibraryQuery) => {
    if (!query.system_default) return;
    await patchQuery(query, {
      enabled: query.system_default.enabled,
      interval_minutes: query.system_default.interval_minutes,
    });
    load();
  };

  const removeQuery = async (query: OsqueryLibraryQuery) => {
    if (!window.confirm(`Delete "${query.name}"? Agents will stop collecting it.`)) return;
    setBusyId(query.id);
    setError(null);
    try {
      await apiService.deleteOsqueryLibraryQuery(query.id);
      setQueries((prev) => prev.filter((q) => q.id !== query.id));
      flash(`Deleted "${query.name}".`);
    } catch (err) {
      setError(errorText(err, 'Failed to delete the query'));
    } finally {
      setBusyId(null);
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSavingDraft(true);
    setError(null);
    try {
      if (draft.id) {
        const updated = await apiService.updateOsqueryLibraryQuery(draft.id, {
          name: draft.name,
          sql: draft.sql,
          description: draft.description,
          category: draft.category,
          platforms: draft.platforms,
          interval_minutes: draft.interval_minutes,
          enabled: draft.enabled,
        });
        setQueries((prev) => prev.map((q) => (q.id === draft.id ? { ...q, ...updated } : q)));
        flash(`Saved "${draft.name}".`);
      } else {
        const created = await apiService.createOsqueryLibraryQuery({
          name: draft.name,
          sql: draft.sql,
          description: draft.description,
          category: draft.category,
          platforms: draft.platforms,
          interval_minutes: draft.interval_minutes,
          enabled: draft.enabled,
        });
        setQueries((prev) => [...prev, created]);
        flash(`Added "${draft.name}".`);
      }
      setDraft(null);
    } catch (err) {
      setError(errorText(err, 'Failed to save the query'));
    } finally {
      setSavingDraft(false);
    }
  };

  const showPreview = async (platform: string) => {
    setError(null);
    try {
      const res = await apiService.getOsqueryResolvedQueries(platform);
      setPreview({ platform, names: res.queries.map((q) => q.name) });
    } catch (err) {
      setError(errorText(err, 'Failed to resolve the query list'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-600">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-blue-600" />
        Loading query library...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h4 className="font-medium text-slate-800">Query library</h4>
          <p className="text-sm text-slate-500">
            Each query is saved on its own. System queries ship with UEM and are shared; you can
            turn one off or change how often it runs, but not rewrite its SQL.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setDraft({ ...EMPTY_DRAFT })}
            className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            <Plus size={16} />
            Add query
          </button>
        )}
      </div>

      {notice && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <CheckCircle size={18} />
          {notice}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        >
          <option value="">All platforms</option>
          {platforms.map((p) => (
            <option key={p} value={p}>
              {PLATFORM_LABELS[p] || p}
            </option>
          ))}
        </select>
        <span className="text-sm text-slate-500">
          {visible.length} of {queries.length} queries
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-slate-500">Preview what an agent runs:</span>
          {platforms.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => showPreview(p)}
              className="flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50"
            >
              <Eye size={13} />
              {PLATFORM_LABELS[p] || p}
            </button>
          ))}
        </div>
      </div>

      {preview && (
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-700">
              {PLATFORM_LABELS[preview.platform] || preview.platform} agents run{' '}
              {preview.names.length} quer{preview.names.length === 1 ? 'y' : 'ies'}
            </span>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="text-slate-500 hover:text-slate-700 text-xs"
            >
              Close
            </button>
          </div>
          <p className="mt-1 text-slate-600 font-mono text-xs">
            {preview.names.join(', ') || 'None — every query is disabled or targets another platform.'}
          </p>
        </div>
      )}

      {draft && (
        <div className="p-4 bg-white rounded-lg border-2 border-blue-200 space-y-3">
          <h5 className="font-medium text-slate-800">
            {draft.id ? `Edit ${draft.name}` : 'New query'}
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Name (e.g. listening_ports)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={10080}
                value={draft.interval_minutes}
                onChange={(e) =>
                  setDraft({ ...draft, interval_minutes: parseInt(e.target.value, 10) || 15 })
                }
                className="w-24 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
              <span className="text-sm text-slate-500">minutes</span>
            </div>
          </div>
          <input
            type="text"
            placeholder="Description (optional)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
          <textarea
            value={draft.sql}
            onChange={(e) => setDraft({ ...draft, sql: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
          />
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-slate-600">Platforms:</span>
            {platforms.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.platforms.includes(p)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      platforms: e.target.checked
                        ? [...draft.platforms, p]
                        : draft.platforms.filter((x) => x !== p),
                    })
                  }
                  className="rounded border-slate-300 text-blue-600"
                />
                {PLATFORM_LABELS[p] || p}
              </label>
            ))}
            <span className="text-xs text-slate-400">None selected means every platform.</span>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={saveDraft}
              disabled={savingDraft || !draft.name.trim() || !draft.sql.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {savingDraft ? 'Saving...' : 'Save query'}
            </button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {grouped.map(([category, items]) => (
          <div key={category}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  CATEGORY_STYLES[category] || CATEGORY_STYLES.general
                }`}
              >
                {category}
              </span>
              <span className="text-xs text-slate-400">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((q) => (
                <div
                  key={q.id}
                  className={`rounded-lg border ${
                    q.enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3 p-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(q.id)}
                      className="text-slate-400 hover:text-slate-600"
                      title="Show SQL"
                    >
                      {expanded.has(q.id) ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`font-mono text-sm ${
                            q.enabled ? 'text-slate-800' : 'text-slate-400'
                          }`}
                        >
                          {q.name}
                        </span>
                        {q.is_system && (
                          <span
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600"
                            title="Ships with UEM; SQL is shared across tenants"
                          >
                            <Lock size={11} />
                            system
                          </span>
                        )}
                        {q.overridden && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                            customised
                          </span>
                        )}
                        {(q.platforms.length ? q.platforms : ['all']).map((p) => (
                          <span
                            key={p}
                            className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100"
                          >
                            {p === 'all' ? 'all platforms' : PLATFORM_LABELS[p] || p}
                          </span>
                        ))}
                      </div>
                      {q.description && (
                        <p className="text-xs text-slate-500 mt-0.5">{q.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <input
                        type="number"
                        min={1}
                        max={10080}
                        defaultValue={q.interval_minutes}
                        disabled={!canEdit || busyId === q.id}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (value && value !== q.interval_minutes) {
                            patchQuery(q, { interval_minutes: value });
                          }
                        }}
                        className="w-16 px-2 py-1 border border-slate-300 rounded text-sm disabled:bg-slate-100"
                        title="Interval in minutes"
                      />
                      <span className="text-xs text-slate-400 w-8">min</span>

                      <button
                        type="button"
                        disabled={!canEdit || busyId === q.id}
                        onClick={() => patchQuery(q, { enabled: !q.enabled })}
                        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                          q.enabled ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={q.enabled}
                        title={q.enabled ? 'Disable' : 'Enable'}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 mt-0.5 transform rounded-full bg-white shadow transition ${
                            q.enabled ? 'translate-x-4' : 'translate-x-0.5'
                          }`}
                        />
                      </button>

                      {q.overridden && canEdit && (
                        <button
                          type="button"
                          onClick={() => clearOverride(q)}
                          disabled={busyId === q.id}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Reset to the shipped defaults"
                        >
                          <RotateCcw size={15} />
                        </button>
                      )}
                      {!q.is_system && canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              setDraft({
                                id: q.id,
                                name: q.name,
                                sql: q.sql,
                                description: q.description || '',
                                category: q.category,
                                platforms: q.platforms,
                                interval_minutes: q.interval_minutes,
                                enabled: q.enabled,
                              })
                            }
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeQuery(q)}
                            disabled={busyId === q.id}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {expanded.has(q.id) && (
                    <pre className="px-4 pb-3 text-xs font-mono text-slate-600 whitespace-pre-wrap break-all">
                      {q.sql}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {visible.length === 0 && (
          <p className="text-sm text-slate-500 py-6 text-center">
            No queries match these filters.
          </p>
        )}
      </div>
    </div>
  );
};

export default OsqueryQueryLibrary;
